import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi } from "ag-grid-community";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import { Switch } from "@mui/material";
import { Input, Label, TextArea } from "../../components/shared/InputField";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  ButtonSecondary,
  Error,
  HStack,
  VStack,
} from "../../components/utils";
import {
  BonusRatioChangeGrantDetail,
  CorporateActionDetails,
} from "../../types/CompanyProfile";
import { CollapsibleComponent } from "../../components/shared/Collapsible";
import {
  useApproveBonusRatioChange,
  useDiscardBonusRatioChange,
  usePreviewBonusRatioChange,
} from "../../queries/onBoarding";
import { useEsopPlans } from "../../queries";
import { EsopPlan } from "../../types/EsopPlan";
import { formatDisplayDate } from "../../utils/date";
import { ConversionMethodology } from "../../types/Exercise";
import {
  EmploymentStatus,
  EmploymentType,
  NameAndId,
  UploadedFile,
} from "../../types/Transaction";
import { GrantState } from "../../types/common";
import { Select } from "../../components/shared/Select";
import { TooltipProps } from "../../components/shared/Tooltip";
import { formatCurrency, getCurrencyType } from "../../utils/currencyFormatter";
import { BonusSummaryReport } from "./CorporateActionDetailsCard";
import GenericFilterBar, { GenericFilterType } from "./utils/Filters";
import { queryClient } from "../../queries/client";

export function AddOrEditBonusRatioChangeDetails({
  corporateAction,
  mode,
  onClose,
  setDialog,
  hideHeader,
}: {
  corporateAction?: CorporateActionDetails;
  mode:
    | "AddBonusRatioChange"
    | "EditBonusRatioChange"
    | "ApproveBonusRatioChange";
  onClose: () => void;
  setDialog: Function;
  hideHeader?: boolean;
}) {
  const currencyType = getCurrencyType();

  const initialValues: CorporateActionDetails = {
    id: corporateAction?.id,
    name: corporateAction?.name || "",
    date: corporateAction?.date
      ? new Date(corporateAction.date).toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0],
    ratio: corporateAction?.ratio || 1,
    type: corporateAction?.type || "BONUS_RATIO_CHANGE",
    state: corporateAction?.state || "DRAFT",
    roundingMethodology:
      corporateAction?.roundingMethodology || ConversionMethodology.ROUND_DOWN,
    note: corporateAction?.note || "",
    settings: corporateAction?.settings || {
      generateBonusRatioChangeLetter: false,
      grantLevelLetterGeneration: true,
      employeeSignatureRequired: false,
      notifyEmployees: false,
      notifyOnEmployeePortal: false,
    },
    metaData: corporateAction?.metaData || {
      filters: {},
      bonusRatioChangeDetails: {
        ratioMode: "MULTIPLICATIVE",
        planRatioOverrides: {},
        grantRatioOverrides: {},
      },
    },
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    date: Yup.date().required("Date is required"),
    ratio: Yup.number()
      .positive("Ratio must be positive")
      .required("Base Ratio is required"),
  });

  const [accordionOpened, setAccordionOpened] = useState<{
    [key: string]: boolean;
  }>({
    basicDetails: false,
    applyTo: true,
    ratioOverrides: true,
    documents: true,
    grantPreview: false,
    settings: true,
  });

  const [disableForm, setDisableForm] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [isApproveLoading, setIsApproveLoading] = useState(false);

  const { mutate: previewBonusRatioChange } = usePreviewBonusRatioChange();
  const { mutate: approveBonusRatioChange } = useApproveBonusRatioChange();
  const { mutate: discardBonusRatioChange } = useDiscardBonusRatioChange();

  const { data: esopPlans } = useEsopPlans(true);
  const [companiesPoolSize, setCompaniesPoolSize] = useState<{
    existingTotalPoolSize?: number;
    minimumPoolSizeRequired?: number;
  }>({});

  const [previewGrantDetails, setPreviewGrantDetails] = useState<
    BonusRatioChangeGrantDetail[]
  >([]);
  const [savedCorporateActionId, setSavedCorporateActionId] = useState<
    string | undefined
  >(corporateAction?.id);
  const [lastSavedValues, setLastSavedValues] =
    useState<CorporateActionDetails>(initialValues);

  useEffect(() => {
    setDisableForm(corporateAction?.state === "APPROVED");
  }, [corporateAction?.state]);

  const gridApi = useRef<GridApi | null>(null);
  const onGridReady = (params: any) => {
    gridApi.current = params.api;
  };

  const handleAccordionClick = (section: string) => {
    setAccordionOpened((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleSaveAndPreview = (values: CorporateActionDetails) => {
    if (disableForm) return;
    setIsSaveLoading(true);
    previewBonusRatioChange(values, {
      onSuccess: (data) => {
        const savedValues = { ...values, id: data.corporateActionId };
        setSavedCorporateActionId(data.corporateActionId);
        setLastSavedValues(savedValues);
        formik.setFieldValue("id", data.corporateActionId);
        setPreviewGrantDetails(data.grantDetails || []);
        setAccordionOpened((prev) => ({ ...prev, grantPreview: false }));
        queryClient.invalidateQueries("getAllCorporateActions");
        queryClient.refetchQueries("getAllCorporateActions");
        toast.success("Preview generated and draft saved successfully");
        setIsSaveLoading(false);
        onClose();
      },
      onError: (err: any) => {
        toast.error(
          err?.response?.data?.errorRaw || "Error generating preview"
        );
        setIsSaveLoading(false);
      },
    });
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSaveAndPreview,
    validationSchema,
    enableReinitialize: true,
  });

  useEffect(() => {
    if (esopPlans && esopPlans.length > 0) {
      const total = esopPlans.reduce(
        (acc, plan) =>
          acc + ((plan.totalShares || 0) - (plan.sharesManagedByTrust || 0)),
        0
      );
      setCompaniesPoolSize((prev) => ({
        ...prev,
        existingTotalPoolSize: total,
      }));
    }
  }, [esopPlans]);

  useEffect(() => {
    if (
      !esopPlans ||
      formik.values.metaData?.bonusRatioChangeDetails?.ratioMode !==
        "MULTIPLICATIVE"
    )
      return;
    const overrides =
      formik.values.metaData?.bonusRatioChangeDetails?.planRatioOverrides || {};
    const planIds = (formik.values.metaData?.filters?.planIds || []).map(
      (p: NameAndId) => p.id
    );
    const baseRatio = formik.values.ratio || 1;
    const filteredPlans =
      planIds.length > 0
        ? esopPlans.filter((p) => planIds.includes(p.esopPlanId))
        : esopPlans;
    const required = filteredPlans.reduce((acc, plan) => {
      const appliedRatio = overrides[plan.esopPlanId] ?? baseRatio;
      return (
        acc +
        ((plan.totalShares || 0) - (plan.sharesManagedByTrust || 0)) *
          appliedRatio
      );
    }, 0);
    setCompaniesPoolSize((prev) => ({
      ...prev,
      minimumPoolSizeRequired: required,
    }));
  }, [
    esopPlans,
    formik.values.ratio,
    formik.values.metaData?.bonusRatioChangeDetails?.planRatioOverrides,
    formik.values.metaData?.bonusRatioChangeDetails?.ratioMode,
    formik.values.metaData?.filters?.planIds,
  ]);

  function ApproveTooltip(props: TooltipProps) {
    const { text, children, _className, disabled = false } = props;
    return (
      <div className="relative group">
        {children}
        <div
          className={`absolute bottom-0 right-0 flex-col items-center hidden mb-6 rounded-md left-30 ${
            disabled ? "" : "group-hover:flex"
          }`}
        >
          <div
            className={`relative z-20 p-2 text-sm text-black shadow-lg bg-[#f6f9fb] ${_className}`}
          >
            {text}
          </div>
          <div className="w-3 h-3 -mt-2 ml-40 rotate-45 bg-[#f6f9fb]"></div>
        </div>
      </div>
    );
  }

  const canApprove =
    !!savedCorporateActionId &&
    JSON.stringify(lastSavedValues) === JSON.stringify(formik.values);

  return (
    <>
      <div className="bg-white rounded-lg shadow-md">
        {!hideHeader && (
          <div className="px-10 text-lg font-medium border-b py-7">
            <h6 className="flex justify-between">
              <p className="font-bold">Bonus Ratio Change</p>
              <span onClick={onClose} className="cursor-pointer">
                X
              </span>
            </h6>
          </div>
        )}
        <VStack className="w-full px-10 py-5 gap-9">
          <VStack className="gap-6">
            {/* Basic Details */}
            <CollapsibleComponent
              label={"Event Details"}
              labelDetail={""}
              isOpen={accordionOpened.basicDetails}
              onClick={() => handleAccordionClick("basicDetails")}
            >
              <VStack className="gap-3">
                <HStack className="justify-between gap-4">
                  <VStack className="w-1/2">
                    <HStack className="items-center">
                      <Label className="required mr-10 text-sm font-normal whitespace-nowrap">
                        Bonus Name
                      </Label>
                      <VStack className="gap-1 w-full">
                        <Input
                          type="text"
                          {...formik.getFieldProps("name")}
                          disabled={disableForm}
                          className={disableForm ? "cursor-not-allowed" : ""}
                        />
                        {formik.touched.name && formik.errors.name && (
                          <Error text={formik.errors.name} />
                        )}
                      </VStack>
                    </HStack>
                  </VStack>
                  <VStack className="w-1/2">
                    <HStack className="items-center">
                      <Label className="required mr-10 text-sm font-normal whitespace-nowrap">
                        Bonus Date
                      </Label>
                      <VStack className="gap-1 w-full">
                        <Input
                          type="date"
                          {...formik.getFieldProps("date")}
                          disabled={disableForm}
                          className={disableForm ? "cursor-not-allowed" : ""}
                          onChange={(e) => {
                            const selectedDate = e.target.value;
                            formik.setFieldValue("date", selectedDate);
                            formik.setFieldValue(
                              "metaData.filters.date",
                              selectedDate
                            );
                          }}
                        />
                        {formik.touched.date && formik.errors.date && (
                          <Error text={formik.errors.date} />
                        )}
                      </VStack>
                    </HStack>
                  </VStack>
                </HStack>
                <HStack className="gap-4">
                  <VStack className="w-1/2">
                    <HStack className="items-center">
                      <Label className="required mr-10 text-sm font-normal whitespace-nowrap">
                        Bonus Ratio
                      </Label>
                      <VStack className="gap-1 w-full">
                        <HStack className="items-center form-input px-0 py-0">
                          <p className="text-sm font-normal pl-3 whitespace-nowrap">
                            1 :
                          </p>
                          <Input
                            type="number"
                            step="0.0001"
                            {...formik.getFieldProps("ratio")}
                            disabled={disableForm}
                            className={`${
                              disableForm ? "cursor-not-allowed" : ""
                            } pl-2`}
                          />
                        </HStack>
                        {formik.touched.ratio && formik.errors.ratio && (
                          <Error text={formik.errors.ratio} />
                        )}
                      </VStack>
                    </HStack>
                    <HStack className="justify-end pt-1">
                      {formik.values.ratio > 0 && (
                        <p className="text-xs font-semi-medium text-gray-light">
                          {formik.values.ratio} bonus shares for every 1
                          equivalent share of option.
                        </p>
                      )}
                    </HStack>
                  </VStack>
                  {formik.values.ratio &&
                    formik.values.ratio !== Math.floor(formik.values.ratio) && (
                      <VStack className="w-1/2">
                        <HStack className="items-center">
                          <Label className="mr-10 text-sm font-normal whitespace-nowrap">
                            Rounding Methodology
                          </Label>
                          <VStack className="gap-1 w-full">
                            <Select
                              options={[
                                ConversionMethodology.ROUND_DOWN,
                                ConversionMethodology.ROUND_UP,
                                "ROUND_OFF",
                                "NONE",
                              ]}
                              textGetter={(e) => e.replaceAll("_", " ")}
                              {...formik.getFieldProps("roundingMethodology")}
                              disabled={disableForm}
                            />
                          </VStack>
                        </HStack>
                      </VStack>
                    )}
                </HStack>
                <HStack className="gap-4">
                  <VStack className="w-1/2">
                    <HStack className="items-center">
                      <Label className="mr-10 text-sm font-normal whitespace-nowrap">
                        Notes
                      </Label>
                      <VStack className="gap-1 w-full">
                        <TextArea
                          {...formik.getFieldProps("note")}
                          disabled={disableForm}
                          className={disableForm ? "cursor-not-allowed" : ""}
                        />
                      </VStack>
                    </HStack>
                  </VStack>
                </HStack>
                <HStack className="gap-4 w-1/2"></HStack>
              </VStack>
            </CollapsibleComponent>

            {/* Apply To Filters */}
            <CollapsibleComponent
              label={"Filter Grants"}
              labelDetail={"Scope which grants this bonus applies to"}
              isOpen={accordionOpened.applyTo}
              onClick={() => handleAccordionClick("applyTo")}
            >
              <BonusRatioChangeApplyToFilters
                formik={formik}
                disableForm={disableForm}
                esopPlans={esopPlans}
              />
            </CollapsibleComponent>

            {/* Plan and Pool Overrides */}
            <CollapsibleComponent
              label={"Plan and Pool Overrides (Optional)"}
              labelDetail={""}
              isOpen={accordionOpened.ratioOverrides}
              onClick={() => handleAccordionClick("ratioOverrides")}
            >
              <VStack className="gap-2">
                <HStack className="items-center gap-4">
                  <Label className="mr-10 text-sm font-normal whitespace-nowrap">
                    Override Pool Size (Optional)
                  </Label>
                  <VStack className="gap-1 w-72">
                    <Input
                      type="number"
                      step="1"
                      placeholder="Leave blank to use existing pool size"
                      value={
                        formik.values.metaData?.bonusRatioChangeDetails
                          ?.overridenPoolSize ?? ""
                      }
                      onChange={(e) =>
                        formik.setFieldValue(
                          "metaData.bonusRatioChangeDetails.overridenPoolSize",
                          e.target.value ? Number(e.target.value) : undefined
                        )
                      }
                      disabled={disableForm}
                      className={disableForm ? "cursor-not-allowed" : ""}
                    />
                  </VStack>
                </HStack>
                <PlanRatioOverrides
                  formik={formik}
                  disableForm={disableForm}
                  esopPlans={esopPlans}
                />
                <HStack className="items-start gap-2 mt-1 p-2 bg-blue-50 border border-blue-200 rounded-md">
                  <Icon
                    icon="material-symbols:info-outline"
                    className="text-blue-500 flex-shrink-0 mt-0.5"
                    width={16}
                    height={16}
                  />
                  <p className="text-xs text-blue-700">
                    The overridden ratio will apply only to grants under the
                    selected plan. Grants not part of the current bonus will
                    continue to follow the existing ratio.
                  </p>
                </HStack>
              </VStack>
            </CollapsibleComponent>

            {/* Documents */}
            <CollapsibleComponent
              label={"Documents"}
              labelDetail={""}
              isOpen={accordionOpened.documents}
              onClick={() => handleAccordionClick("documents")}
            >
              <BonusRatioChangeDocumentsSection
                formik={formik}
                disableForm={disableForm}
                s3url={corporateAction?.s3url}
              />
            </CollapsibleComponent>

            {/* Grant Preview */}
            <div className="hidden">
              <CollapsibleComponent
                label={"Grant Preview"}
                labelDetail={
                  previewGrantDetails.length > 0
                    ? `${previewGrantDetails.length} grants affected`
                    : "Save to generate preview"
                }
                isOpen={accordionOpened.grantPreview}
                onClick={() => handleAccordionClick("grantPreview")}
              >
                <BonusRatioChangeGrantGrid
                  grantDetails={previewGrantDetails}
                  onGridReady={onGridReady}
                  currencyType={currencyType}
                  formik={formik}
                  disableForm={disableForm}
                />
              </CollapsibleComponent>
            </div>

            {/* Notification Settings */}
            <CollapsibleComponent
              label={"Notification Settings"}
              labelDetail={""}
              isOpen={accordionOpened.settings}
              onClick={() => handleAccordionClick("settings")}
            >
              <BonusRatioChangeNotificationSettings
                formik={formik}
                disableForm={disableForm}
              />
            </CollapsibleComponent>

            <BonusRatioChangeWarningNotes />

            {companiesPoolSize.existingTotalPoolSize !== undefined &&
              companiesPoolSize.minimumPoolSizeRequired !== undefined &&
              companiesPoolSize.existingTotalPoolSize <
                companiesPoolSize.minimumPoolSizeRequired &&
              corporateAction?.state !== "APPROVED" &&
              formik.values.metaData?.bonusRatioChangeDetails?.ratioMode ===
                "MULTIPLICATIVE" && (
                <VStack className="p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm hidden">
                  <HStack className="items-start gap-3">
                    <Icon
                      icon="material-symbols:warning-rounded"
                      className="text-red-500 flex-shrink-0 mt-0.5"
                      width={24}
                      height={24}
                    />
                    <VStack className="gap-2 flex-1">
                      <h4 className="text-sm font-semibold text-red-900">
                        Insufficient Pool Size
                      </h4>
                      <p className="text-sm text-red-800">
                        The bonus ratio change requires additional pool
                        capacity. Please ensure sufficient pool size before
                        approving.
                      </p>
                      <HStack className="gap-6 mt-2 p-3 bg-white rounded border border-red-200">
                        <VStack className="gap-1">
                          <span className="text-xs text-gray-600 font-medium">
                            Current Pool Size
                          </span>
                          <span className="text-base font-semibold text-gray-900">
                            {companiesPoolSize.existingTotalPoolSize.toLocaleString(
                              currencyType
                            )}
                          </span>
                        </VStack>
                        <Icon
                          icon="material-symbols:arrow-forward"
                          className="text-red-400 mt-4"
                          width={20}
                        />
                        <VStack className="gap-1">
                          <span className="text-xs text-gray-600 font-medium">
                            Required Pool Size
                          </span>
                          <span className="text-base font-semibold text-red-600">
                            {formik.values.roundingMethodology ===
                            ConversionMethodology.ROUND_DOWN
                              ? Math.floor(
                                  companiesPoolSize.minimumPoolSizeRequired
                                ).toLocaleString(currencyType)
                              : companiesPoolSize.minimumPoolSizeRequired.toLocaleString(
                                  currencyType
                                )}
                          </span>
                        </VStack>
                        <VStack className="gap-1 ml-4">
                          <span className="text-xs text-gray-600 font-medium">
                            Additional Needed
                          </span>
                          <span className="text-base font-semibold text-red-700">
                            {(formik.values.roundingMethodology ===
                            ConversionMethodology.ROUND_DOWN
                              ? Math.floor(
                                  companiesPoolSize.minimumPoolSizeRequired -
                                    companiesPoolSize.existingTotalPoolSize
                                )
                              : companiesPoolSize.minimumPoolSizeRequired -
                                companiesPoolSize.existingTotalPoolSize
                            ).toLocaleString(currencyType)}
                          </span>
                        </VStack>
                      </HStack>
                    </VStack>
                  </HStack>
                </VStack>
              )}

            <HStack className="justify-between mt-4">
              <ButtonSecondary className="h-8 px-2 py-1" onClick={onClose}>
                Cancel
              </ButtonSecondary>
              <HStack className="gap-5">
                <ButtonPrimary1
                  loading={isSaveLoading}
                  disabled={
                    disableForm ||
                    isSaveLoading ||
                    JSON.stringify(lastSavedValues) ===
                      JSON.stringify(formik.values)
                  }
                  onClick={() => formik.handleSubmit()}
                  type="button"
                >
                  Save & Preview
                </ButtonPrimary1>

                {(mode === "EditBonusRatioChange" ||
                  mode === "AddBonusRatioChange") &&
                  corporateAction?.state !== "APPROVED" && (
                    <ApproveTooltip
                      text={
                        !savedCorporateActionId
                          ? "Save and generate preview before approving"
                          : JSON.stringify(lastSavedValues) !==
                            JSON.stringify(formik.values)
                          ? "Please save details to approve"
                          : // : previewGrantDetails.length === 0
                            // ? "No grants found for the given filters"
                            "Approving will apply ratio changes to all filtered grants"
                      }
                      _className="whitespace-nowrap"
                    >
                      <ButtonPrimary
                        loading={isApproveLoading}
                        disabled={
                          !canApprove || disableForm || isApproveLoading
                        }
                        onClick={() => {
                          if (!canApprove) return;
                          setDialog({
                            open: true,
                            data: {
                              ...formik.values,
                              id: savedCorporateActionId,
                            },
                            mode: "ApproveBonusRatioChange",
                          });
                        }}
                        type="button"
                      >
                        Approve
                      </ButtonPrimary>
                    </ApproveTooltip>
                  )}
              </HStack>
            </HStack>
          </VStack>
        </VStack>
      </div>
    </>
  );
}

function PlanRatioOverrides({
  formik,
  disableForm,
  esopPlans,
}: {
  formik: any;
  disableForm: boolean;
  esopPlans: EsopPlan[] | undefined;
}) {
  const overrides: Record<string, number> =
    formik.values.metaData?.bonusRatioChangeDetails?.planRatioOverrides || {};
  const baseRatio = formik.values.ratio || 1;

  const handleOverrideChange = (planId: string, value: string) => {
    const current: Record<string, number> = {
      ...(formik.values.metaData?.bonusRatioChangeDetails?.planRatioOverrides ||
        {}),
    };
    if (value === "") {
      delete current[planId];
    } else {
      current[planId] = Number(value);
    }
    formik.setFieldValue(
      "metaData.bonusRatioChangeDetails.planRatioOverrides",
      current
    );
  };

  const overrideCount = Object.keys(overrides).length;

  if (!esopPlans || esopPlans.length === 0) {
    return (
      <VStack className="gap-2 pt-2">
        <p className="text-xs text-gray-light">No plans available.</p>
      </VStack>
    );
  }

  const selectedPlanIds = (formik.values.metaData?.filters?.planIds || []).map(
    (p: any) => p.id
  );
  const plansToDisplay =
    selectedPlanIds.length > 0
      ? esopPlans?.filter((p) => selectedPlanIds.includes(p.esopPlanId))
      : esopPlans;

  return (
    <VStack className="gap-3 pt-2">
      <HStack className="items-center justify-between">
        <p className="text-xs text-gray-light">
          Set a specific ratio for individual plans. Leave blank to use the base
          ratio (1:{baseRatio}).
        </p>
        {overrideCount > 0 && (
          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
            {overrideCount} override{overrideCount !== 1 ? "s" : ""} set
          </span>
        )}
      </HStack>
      <div className="w-full overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50 text-left">
              <th className="px-3 py-2 font-medium text-gray-600 border-b">
                Plan Name
              </th>
              <th className="px-3 py-2 font-medium text-gray-600 border-b">
                Plan Type
              </th>
              <th className="px-3 py-2 font-medium text-gray-600 border-b w-44">
                Override Ratio
              </th>
            </tr>
          </thead>
          <tbody>
            {plansToDisplay?.map((plan) => (
              <tr key={plan.esopPlanId} className="border-b hover:bg-gray-50">
                <td className="px-3 py-2">{plan.planName}</td>
                <td className="px-3 py-2 text-gray-500">
                  {plan.planType?.replaceAll("_", " ") || "-"}
                </td>
                <td className="px-3 py-2">
                  <Input
                    type="number"
                    step="0.0001"
                    min="0"
                    placeholder={(() => {
                      const val = plan.conversionShares * (baseRatio + 1);
                      return val % 1 === 0
                        ? String(val)
                        : String(Number(val.toFixed(4)));
                    })()}
                    value={overrides[plan.esopPlanId] ?? ""}
                    onChange={(e) =>
                      handleOverrideChange(plan.esopPlanId, e.target.value)
                    }
                    disabled={disableForm}
                    className={`w-32 ${
                      disableForm ? "cursor-not-allowed" : ""
                    }`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </VStack>
  );
}

function BonusRatioChangeGrantGrid({
  grantDetails,
  onGridReady,
  currencyType,
  formik,
  disableForm,
}: {
  grantDetails: BonusRatioChangeGrantDetail[];
  onGridReady: any;
  currencyType?: string;
  formik: any;
  disableForm: boolean;
}) {
  const [filters, setFilters] = useState<GenericFilterType[]>([]);
  const [filteredData, setFilteredData] = useState<
    BonusRatioChangeGrantDetail[]
  >([]);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Grant ID",
        field: "identifier",
        sortable: true,
        filter: true,
        minWidth: 120,
      },
      {
        headerName: "Holder Name",
        field: "employeeName",
        sortable: true,
        filter: true,
        minWidth: 150,
      },
      {
        headerName: "Plan",
        field: "planName",
        sortable: true,
        filter: true,
        minWidth: 140,
      },
      {
        headerName: "Grant Date",
        field: "dateOfGrant",
        valueFormatter: (params) =>
          params.value ? formatDisplayDate(params.value) : "",
        sortable: true,
        filter: true,
        minWidth: 120,
      },
      {
        headerName: "Pre Ratio",
        field: "preRatio",
        sortable: true,
        filter: true,
        minWidth: 100,
      },
      {
        headerName: "Applied Ratio",
        field: "appliedRatio",
        sortable: true,
        filter: true,
        minWidth: 110,
      },
      {
        headerName: "Post Ratio",
        field: "postRatio",
        sortable: true,
        filter: true,
        minWidth: 100,
      },
      {
        headerName: "Ratio Mode",
        field: "ratioMode",
        valueFormatter: (params) =>
          params.value ? params.value.replaceAll("_", " ") : "",
        sortable: true,
        filter: true,
        minWidth: 120,
      },
      {
        headerName: "Options Granted",
        field: "optionsGranted",
        valueFormatter: (params) =>
          Math.floor(params.value || 0).toLocaleString(currencyType),
        sortable: true,
        filter: true,
        minWidth: 130,
      },
      {
        headerName: "Pre Equiv. Granted Shares",
        field: "preEquivalentGrantedShares",
        valueFormatter: (params) =>
          Math.floor(params.value || 0).toLocaleString(currencyType),
        sortable: true,
        filter: true,
        minWidth: 180,
      },
      {
        headerName: "Post Equiv. Granted Shares",
        field: "postEquivalentGrantedShares",
        valueFormatter: (params) =>
          Math.floor(params.value || 0).toLocaleString(currencyType),
        sortable: true,
        filter: true,
        minWidth: 180,
      },
      {
        headerName: "Pre Equiv. Active Shares",
        field: "preEquivalentActiveShares",
        valueFormatter: (params) =>
          Math.floor(params.value || 0).toLocaleString(currencyType),
        sortable: true,
        filter: true,
        minWidth: 170,
      },
      {
        headerName: "Post Equiv. Active Shares",
        field: "postEquivalentActiveShares",
        valueFormatter: (params) =>
          Math.floor(params.value || 0).toLocaleString(currencyType),
        sortable: true,
        filter: true,
        minWidth: 170,
      },
      {
        headerName: "Pre Strike Price",
        field: "preStrikePrice",
        sortable: true,
        filter: true,
        valueFormatter: (params) =>
          params.value != null
            ? formatCurrency(
                Number(params.value.toFixed(2)),
                currencyType || ""
              )
            : "",
        minWidth: 130,
      },
      {
        headerName: "Post Strike Price",
        field: "postStrikePrice",
        sortable: true,
        filter: true,
        valueFormatter: (params) =>
          params.value != null
            ? formatCurrency(
                Number(params.value.toFixed(2)),
                currencyType || ""
              )
            : "",
        minWidth: 140,
      },
      {
        headerName: "Grant State",
        field: "grantState",
        sortable: true,
        filter: true,
        minWidth: 120,
      },
      {
        headerName: "Employment Status",
        field: "employeeStatus",
        sortable: true,
        filter: true,
        minWidth: 150,
      },
    ],
    [currencyType]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      wrapText: true,
      flex: 1,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: "multiline",
      columnsMenuParams: { suppressColumnFilter: true },
      cellStyle: () => ({ paddingTop: "15px", lineHeight: "20px" }),
      filterParams: { buttons: [] },
      menuTabs: [],
      minWidth: 100,
      filter: true,
      resizable: true,
    }),
    []
  );

  // Build filters from grant data
  useEffect(() => {
    const uniquePlanIds: NameAndId[] = [];
    const employmentStatusOptions: string[] = [];
    const employmentTypesOptions: string[] = [];
    const planTypesOptions: string[] = [];
    const grantStateOptions: string[] = [];
    const newFilters: GenericFilterType[] = [];

    for (const grant of grantDetails) {
      if (
        grant.planId &&
        grant.planName &&
        !uniquePlanIds.find((p) => p.id === grant.planId)
      ) {
        uniquePlanIds.push({ id: grant.planId, name: grant.planName });
      }
      if (
        grant.employeeStatus &&
        !employmentStatusOptions.includes(grant.employeeStatus)
      ) {
        employmentStatusOptions.push(grant.employeeStatus);
      }
      if (
        grant.employmentType &&
        !employmentTypesOptions.includes(grant.employmentType)
      ) {
        employmentTypesOptions.push(grant.employmentType);
      }
      if (grant.planType && !planTypesOptions.includes(grant.planType)) {
        planTypesOptions.push(grant.planType);
      }
      if (grant.grantState && !grantStateOptions.includes(grant.grantState)) {
        grantStateOptions.push(grant.grantState);
      }
    }

    if (uniquePlanIds.length > 0) {
      newFilters.push({
        label: "Plans",
        id: "planIds",
        options: uniquePlanIds,
        type: "multiCheckboxNameAndId",
        value: formik.values.metaData?.filters?.planIds || [],
        onChange: (selectedValues: NameAndId[]) => {
          formik.setFieldValue("metaData.filters.planIds", selectedValues);
        },
      });
    }
    if (employmentStatusOptions.length > 0) {
      newFilters.push({
        label: "Employment Status",
        id: "employmentStatus",
        options: employmentStatusOptions,
        type: "multiCheckbox",
        value: formik.values.metaData?.filters?.employmentStatus || [],
        onChange: (selectedValues: string[]) => {
          formik.setFieldValue(
            "metaData.filters.employmentStatus",
            selectedValues
          );
        },
      });
    }
    if (employmentTypesOptions.length > 0) {
      newFilters.push({
        label: "Employment Type",
        id: "employmentType",
        options: employmentTypesOptions,
        type: "multiCheckbox",
        value: formik.values.metaData?.filters?.employmentType || [],
        onChange: (selectedValues: string[]) => {
          formik.setFieldValue(
            "metaData.filters.employmentType",
            selectedValues
          );
        },
      });
    }
    if (planTypesOptions.length > 0) {
      newFilters.push({
        label: "Plan Type",
        id: "planType",
        options: planTypesOptions,
        type: "multiCheckbox",
        value: formik.values.metaData?.filters?.planType || [],
        onChange: (selectedValues: string[]) => {
          formik.setFieldValue("metaData.filters.planType", selectedValues);
        },
      });
    }
    if (grantStateOptions.length > 0) {
      newFilters.push({
        label: "Grant State",
        id: "grantState",
        options: grantStateOptions,
        type: "multiCheckbox",
        value: formik.values.metaData?.filters?.grantState || [],
        onChange: (selectedValues: string[]) => {
          formik.setFieldValue("metaData.filters.grantState", selectedValues);
        },
      });
    }
    setFilters(newFilters);
  }, [grantDetails, formik.values.metaData?.filters]);

  // Apply client-side filters to preview data
  useEffect(() => {
    const { filters: activeFilters = {} } = formik.values?.metaData || {};
    let data = [...grantDetails];

    if (activeFilters.planIds && activeFilters.planIds.length > 0) {
      const planIds = activeFilters.planIds.map((p: any) => p.id);
      data = data.filter((row) => planIds.includes(row.planId));
    }
    if (
      activeFilters.employmentStatus &&
      activeFilters.employmentStatus.length > 0
    ) {
      data = data.filter((row) =>
        activeFilters.employmentStatus.includes(row.employeeStatus)
      );
    }
    if (
      activeFilters.employmentType &&
      activeFilters.employmentType.length > 0
    ) {
      data = data.filter((row) =>
        activeFilters.employmentType.includes(row.employmentType)
      );
    }
    if (activeFilters.planType && activeFilters.planType.length > 0) {
      data = data.filter((row) =>
        activeFilters.planType.includes(row.planType)
      );
    }
    if (activeFilters.grantState && activeFilters.grantState.length > 0) {
      data = data.filter((row) =>
        activeFilters.grantState.includes(row.grantState)
      );
    }

    setFilteredData(data);
  }, [grantDetails, formik.values.metaData?.filters]);

  if (grantDetails.length === 0) {
    return (
      <VStack className="py-8 items-center gap-2">
        <Icon
          icon="material-symbols:preview-outline"
          width={40}
          className="text-gray-300"
        />
        <p className="text-sm text-gray-light">
          Click &quot;Save &amp; Preview&quot; to generate a preview of affected
          grants.
        </p>
      </VStack>
    );
  }

  return (
    <>
      {filters.length > 0 && (
        <GenericFilterBar filters={filters} disableUpdate={disableForm} />
      )}
      <HStack className="justify-between w-full mt-2">
        <Box
          style={{
            height: `${Math.max(
              400,
              (filteredData.length >= 10 ? 10 : filteredData.length + 3) * 60
            )}px`,
          }}
          className="w-full h-full max-h-full overflow-x-auto ag-theme-material"
        >
          <AgGridReact
            tooltipShowDelay={0}
            rowData={filteredData}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            defaultColDef={defaultColDef}
            suppressRowTransform={true}
            animateRows={true}
            pagination={false}
            rowClass="border-t border-dashed cursor-pointer hover:bg-slate-50"
            overlayNoRowsTemplate='<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px;">No Rows To Show</span>'
          />
        </Box>
      </HStack>
    </>
  );
}

function BonusRatioChangeNotificationSettings({
  formik,
  disableForm,
}: {
  formik: any;
  disableForm: boolean;
}) {
  return (
    <VStack className="gap-3 lg:w-[700px]">
      <VStack className="w-full">
        <HStack className="items-center justify-between w-full">
          <Label className="mr-10 text-sm font-normal whitespace-nowrap">
            Notify Employees
          </Label>
          <Switch
            checked={formik.values.settings?.notifyEmployees}
            onChange={() =>
              formik.setFieldValue(
                "settings.notifyEmployees",
                !formik.values.settings?.notifyEmployees
              )
            }
            value={formik.values.settings?.notifyEmployees}
            disabled={disableForm}
          />
        </HStack>
        <p className="text-xs font-semi-medium text-gray-light">
          Send email notifications to all affected employees after approval
        </p>
      </VStack>
      <VStack className="w-full hidden">
        <HStack className="items-center justify-between w-full">
          <Label className="mr-10 text-sm font-normal whitespace-nowrap">
            Notify on Employee Portal
          </Label>
          <Switch
            checked={formik.values.settings?.notifyOnEmployeePortal}
            onChange={() =>
              formik.setFieldValue(
                "settings.notifyOnEmployeePortal",
                !formik.values.settings?.notifyOnEmployeePortal
              )
            }
            value={formik.values.settings?.notifyOnEmployeePortal}
            disabled={disableForm}
          />
        </HStack>
        <p className="text-xs font-semi-medium text-gray-light">
          Notify employees on the employee portal when the bonus ratio change is
          approved
        </p>
      </VStack>
    </VStack>
  );
}

function BonusRatioChangeWarningNotes() {
  const notes = [
    `Only grants with dateOfGrant on or before the effective date will be considered.`,
    `Ratio resolution order: grant override → plan override → base ratio.`,
    `Base bonus Ratio: post ratio = pre ratio × applied ratio.`,
    `Override Ratio: post ratio = applied ratio (pre ratio is replaced).`,
    `Strike prices will be adjusted inversely for "Share Price" type grants.`,
    `An APPROVED Bonus Ratio Change can be discarded - this will revert all grant changes.`,
    `Ensure all incomplete events are completed before approving.`,
    `Plan overrides will not be applicable for new grants created after the bonus event.`,
  ];

  return (
    <VStack className="p-2 text-black-501 bg-yellow-100 rounded">
      <HStack className="pt-0 justify-start">
        <HStack className="gap-1 pl-1 items-center">
          <Icon
            icon="material-symbols:info-outline-rounded"
            height={18}
            width={18}
            className="w-full text-xs font-medium text-black-501 rounded"
          />
          <p className="text-sm">Note</p>
        </HStack>
      </HStack>
      <Box className="pl-5 pt-2 pb-1">
        <ul className="list-disc">
          {notes.map((note, index) => (
            <li key={index} className="text-xs pt-0.5">
              {note}
            </li>
          ))}
        </ul>
      </Box>
    </VStack>
  );
}

function BonusRatioChangeApplyToFilters({
  formik,
  disableForm,
  esopPlans,
}: {
  formik: any;
  disableForm: boolean;
  esopPlans: EsopPlan[] | undefined;
}) {
  const filters: GenericFilterType[] = [];

  if (esopPlans && esopPlans.length > 0) {
    filters.push({
      label: "Plans",
      id: "planIds",
      options: esopPlans.map((p) => ({ id: p.esopPlanId, name: p.planName })),
      type: "multiCheckboxNameAndId",
      value: formik.values.metaData?.filters?.planIds || [],
      onChange: (selectedValues: NameAndId[]) => {
        formik.setFieldValue("metaData.filters.planIds", selectedValues);
      },
    });
  }

  filters.push({
    label: "Employment Status",
    id: "employmentStatus",
    options: Object.values(EmploymentStatus),
    type: "multiCheckbox",
    value: formik.values.metaData?.filters?.employmentStatus || [],
    onChange: (selectedValues: string[]) => {
      formik.setFieldValue("metaData.filters.employmentStatus", selectedValues);
    },
  });

  filters.push({
    label: "Employment Type",
    id: "employmentType",
    options: Object.values(EmploymentType),
    type: "multiCheckbox",
    value: formik.values.metaData?.filters?.employmentType || [],
    onChange: (selectedValues: string[]) => {
      formik.setFieldValue("metaData.filters.employmentType", selectedValues);
    },
  });

  filters.push({
    label: "Grant State",
    id: "grantState",
    options: [GrantState.GRANTED, GrantState.OFFERED],
    type: "multiCheckbox",
    value: formik.values.metaData?.filters?.grantState || [],
    onChange: (selectedValues: string[]) => {
      formik.setFieldValue("metaData.filters.grantState", selectedValues);
    },
  });

  return (
    <VStack className="gap-2 pt-2">
      <p className="text-xs text-gray-light">
        Restrict which grants this bonus ratio change applies to. Leave all
        filters empty to include all eligible grants.
      </p>
      <GenericFilterBar filters={filters} disableUpdate={disableForm} />
    </VStack>
  );
}

function BonusRatioChangeDocumentsSection({
  formik,
  disableForm,
  s3url,
}: {
  formik: any;
  disableForm: boolean;
  s3url?: string;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { values, setFieldValue } = formik;
  const uploadedFile: UploadedFile | null =
    values.settings?.templateFile || null;
  const backendFileLink = uploadedFile?.s3Url || undefined;

  const fileToUploadedFile = async (file: File): Promise<UploadedFile> => {
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    return {
      name: file.name,
      type: file.type,
      size: file.size,
      base64,
      isUserUploaded: true,
    };
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const uploaded = await fileToUploadedFile(file);
      setFieldValue("settings.templateFile", uploaded);
    }
  };

  const handleRemoveFile = () => {
    setFieldValue("settings.templateFile", null);
  };

  return (
    <VStack className="gap-4 pt-2 lg:w-[700px]">
      {s3url && (
        <VStack className="w-full gap-1">
          <Label className="text-sm font-normal">Generated Reports</Label>
          <BonusSummaryReport s3url={s3url} />
        </VStack>
      )}
      <VStack className="w-full">
        <HStack className="items-center justify-between w-full">
          <Label className="mr-10 text-sm font-normal whitespace-nowrap">
            Generate Bonus Ratio Change Letter
          </Label>
          <Switch
            checked={values.settings?.generateBonusRatioChangeLetter}
            onChange={() =>
              setFieldValue(
                "settings.generateBonusRatioChangeLetter",
                !values.settings?.generateBonusRatioChangeLetter
              )
            }
            value={values.settings?.generateBonusRatioChangeLetter}
            disabled={disableForm}
          />
        </HStack>
        <p className="text-xs font-semi-medium text-gray-light">
          Generate DOCX/PDF letters for employees affected by this ratio change
        </p>
      </VStack>

      {values.settings?.generateBonusRatioChangeLetter && (
        <VStack className="gap-4">
          <HStack className="gap-6 items-center">
            <Label className="text-sm font-normal">
              Letter Generation Level
            </Label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="bonusLetterLevel"
                value="grant"
                checked={values.settings?.grantLevelLetterGeneration === true}
                disabled={disableForm}
                onChange={() =>
                  setFieldValue("settings.grantLevelLetterGeneration", true)
                }
              />
              Grant Level
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="bonusLetterLevel"
                value="employee"
                checked={values.settings?.grantLevelLetterGeneration === false}
                disabled={disableForm}
                onChange={() =>
                  setFieldValue("settings.grantLevelLetterGeneration", false)
                }
              />
              Employee Level
            </label>
          </HStack>
          <p className="text-xs font-semi-medium text-gray-light -mt-2">
            {values.settings?.grantLevelLetterGeneration
              ? "One letter is generated per grant."
              : "One letter is generated per employee (aggregated across grants)."}
          </p>

          <VStack className="w-full pl-4">
            <HStack className="items-center justify-between w-full">
              <Label className="mr-10 text-sm font-normal whitespace-nowrap">
                Employee Signature Required
              </Label>
              <Switch
                checked={values.settings?.employeeSignatureRequired}
                onChange={() =>
                  setFieldValue(
                    "settings.employeeSignatureRequired",
                    !values.settings?.employeeSignatureRequired
                  )
                }
                value={values.settings?.employeeSignatureRequired}
                disabled={disableForm}
              />
            </HStack>
          </VStack>

          {!uploadedFile && (
            <HStack className="gap-4">
              <ButtonPrimary1
                className={`px-4 py-2 ${
                  disableForm ? "cursor-not-allowed" : ""
                }`}
                onClick={() => fileInputRef.current?.click()}
                disabled={disableForm}
                type="button"
              >
                Upload Letter Template
              </ButtonPrimary1>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx"
                className="hidden"
                onChange={handleFileUpload}
              />
            </HStack>
          )}

          {uploadedFile && (
            <HStack className="gap-4 items-center">
              <a
                href={
                  uploadedFile.base64 ? uploadedFile.base64 : backendFileLink
                }
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline text-sm"
                download={uploadedFile.name}
              >
                {uploadedFile.name}
              </a>
              {!disableForm && (
                <button
                  onClick={handleRemoveFile}
                  className="text-red-500 hover:text-red-700"
                  type="button"
                >
                  <Icon icon="mdi:delete" width={20} height={20} />
                </button>
              )}
            </HStack>
          )}
        </VStack>
      )}
    </VStack>
  );
}

export default AddOrEditBonusRatioChangeDetails;
